<template>
  <div class="user-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t('actions') }}</h2>
      <router-link :to="{ name: 'ActionCreate' }">
        <b-button variant="primary">
          <i class="fas fa-plus mr-2"></i>{{ $t('action.add_action') }}
        </b-button>
      </router-link>
    </div>

    <CrudTable :columns="fields" :data-sources="users" :loader="isLoading" list-url="/actions" ref="action-table">
      <template #cell(action_type)="data">
        {{ Array.isArray(data.item.action_type) ? data.item.action_type.join(', ') : data.item.action_type }}
      </template>
      <template #cell(is_active)="data">
        {{ (data.item.is_active) ? 'active' : 'deactive' }}
      </template>
      <template #cell(notification_type)="data">
        {{ _.join(_.map(data.item.notification_type, _.upperFirst), ", ") }}
      </template>
      <template #cell(triggers)="data">
        <span v-if="data.item.triggers.length">
          {{ data.item.triggers.map(trigger => trigger.name).join(', ') }}
        </span>
        <span v-else>—</span>
      </template>
      <template #cell(start_date)="data">
        {{ data.item.start_date?$global.dateFormat(data.item.start_date,"MMM DD YYYY"):"N/A"}}
      </template>
      <template #cell(end_date)="data">
        {{ data.item.end_date?$global.dateFormat(data.item.end_date,"MMM DD YYYY"):"N/A"}}
      </template>
      <template #cell(email_content)="data">
        <div v-html="data.item.email_content" v-if="data.item.email_content"></div>
        <div v-else> N/A</div>
      </template>
      <template #cell(sms_content)="data">
        <div v-html="data.item.sms_content" v-if="data.item.sms_content"></div>
        <div v-else> N/A</div>
      </template>
      <template #cell(actions)="data">
        <div class="d-flex gap-2">
          <b-button variant="outline-primary" size="sm" :to="{ name: 'ActionEdit', params: { id: data.item.id } }">
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button v-if="data.item.action_type === 'manual'" variant="outline-primary" size="sm"
            @click="sendManualNotification(data.item)" :disabled="isSending[data.item.id]">
            <i class="fas fa-paper-plane"></i> Send Manual
          </b-button>
          <b-button variant="outline-danger" size="sm" @click="confirmDelete(data.item)">
            <i class="fas fa-trash"></i>
          </b-button>

          <b-button
            variant="outline-info"
            size="sm"
            :to="{ name: 'ActionUserCreate', params: { id: data.item.id } }"
            v-if="data.item.action_type == 'manual'"
          >
            {{ $t('action.add_user') }}
          </b-button>
        </div>
      </template>
    </CrudTable>

    <!-- Delete Confirmation Modal -->
    <b-modal v-model="showDeleteModal" :title="$t('action.delete_modal_title')" @ok="handleDelete" ok-variant="danger">
      {{ $t('action.delete_modal_message') }}
    </b-modal>

    <b-modal v-model="showNotificationModal" :title="notificationModalTitle" ok-only>
      <div v-if="notificationResult">
        <div v-if="notificationResult.success" class="text-success">
          <i class="fas fa-check-circle"></i> {{ notificationResult.message }}
        </div>
        <div v-else class="text-danger">
          <i class="fas fa-exclamation-circle"></i> {{ notificationResult.message }}
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from "@/components/CrudTable.vue";

export default {
  name: 'UserList',
  components: {
    CrudTable
  },

  data() {
    return {
      users: [],
      isLoading: false,
      showDeleteModal: false,
      selectedAction: null,
      currentPage: 1,
      perPage: 10,
      sortBy: 'created_at',
      sortDesc: true,

      filters: {
        search: '',
        role: null,
        status: null
      },
      fields: [
        { key: 'name', label: this.$t('action.name'), sortable: true, sortDirection: 'asc' },
        { key: 'action_type', label: this.$t('action.action_type'), sortable: true, sortDirection: 'asc' },
        { key: 'notification_type', label: this.$t('action.notification_type'), sortable: true, sortDirection: 'asc' },
        { key: 'triggers', label: this.$t('action.Triggers'), sortable: false },
        { key: 'start_date', label: this.$t('action.start_date'), sortable: true, sortDirection: 'asc' },
        { key: 'end_date', label: this.$t('action.end_date'), sortable: true, sortDirection: 'asc' },
        { key: 'email_content', label: this.$t('action.email_content'), sortable: false },
        { key: 'sms_content', label: this.$t('action.sms_content'), sortable: false },
        { key: 'is_active', label: this.$t('action.is_active'), sortable: false },
        { key: 'actions', label: this.$t('action.actions'), sortable: false }
      ],
      roleOptions: [],
      statusOptions: [
        { value: null, text: this.$t('user.status_all') },
        { value: 'active', text: this.$t('user.status_active') },
        { value: 'inactive', text: this.$t('user.status_inactive') }
      ],
      isSending: {},
      notificationResult: null,
      showNotificationModal: false,
    }
  },
  computed: {
    notificationModalTitle() {
      if (!this.notificationResult) return 'Notification Status';
      const title = this.notificationResult.success ? 'Success' : 'Error';
      if (this.notificationResult.actionId) {
        return `${title} (Action #${this.notificationResult.actionId})`;
      }
      return title;
    }
  },
  methods: {
    normalizer(node) {
      return {
        id: node.id,
        label: node.label
      };
    },
    getInitials(name) {
      if (!name) return '';
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase();
    },
    handleSearch(search) {
      this.filters.search = search;
      this.currentPage = 1;
    },
    handleFilter(filter) {
      this.filters = { ...this.filters, ...filter };
      this.currentPage = 1;
    },
    confirmDelete(action) {
      this.selectedAction = action;
      this.showDeleteModal = true;
    },
    async handleDelete() {
      if (!this.selectedAction) return;

      try {
        await request({
          method: 'delete',
          url: `/actions/${this.selectedAction.id}`
        });

        this.$refs['action-table'].refreshTableData();
      } catch (error) {
        console.error('Error deleting product:', error);
      } finally {
        this.$bvToast.toast('Action Deleted Successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        this.showDeleteModal = false;
        this.selectedAction = null;
      }
    },
    async sendManualNotification(action) {
      this.isSending[action.id] = true;
      this.notificationResult = null; // Reset previous result

      try {
        const response = await request({
          method: 'post',
          url: `/actions/${action.id}/send-manual`,
        });
        console.log('Manual notification response:', response.message);

        this.$bvToast.toast(response.message || "Manual notifications sent successfully.", {
          title: 'Success',
          variant: 'success'
        });
        this.$refs['action-table'].refreshTableData();

      } catch (error) {
        this.$bvToast.toast(error.response?.message || "Failed to send manual notifications.", {
          title: 'Error',
          variant: 'danger'
        });

      } finally {
        this.isSending[action.id] = false;
      }
    }
  }
}
</script>

<style scoped>
.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}
</style>
